import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Badge, Table, Button,
  Modal, Form, Alert, Spinner,
} from 'react-bootstrap';
import { useInterfaceStore } from '../../stores/useInterfaceStore';
import api from '../../services/api';
import { notify } from '../../stores/useNotificationStore';

interface IPermissao {
  id: number;
  perfil_nome: string;
  modulo: string;
  visualizar: boolean;
  criar: boolean;
  editar: boolean;
  desativar: boolean;
  excluir: boolean;
}

interface IPerfil {
  id: number;
  nome: string;
  label: string;
  descricao: string;
  nivel: number;
  ativo: boolean;
  permissoes: IPermissao[];
}

const nivelBadge = (nivel: number): { bg: string; text: string } => {
  if (nivel >= 80) return { bg: 'danger', text: 'Alto' };
  if (nivel >= 50) return { bg: 'warning', text: 'Médio' };
  return { bg: 'secondary', text: 'Básico' };
};

const CheckIcon: React.FC<{ value: boolean }> = ({ value }) =>
  value
    ? <span className="text-success fw-bold">✓</span>
    : <span className="text-muted">—</span>;

// ── Modal de edição de descrição ──────────────────────────────────────────────

interface EditModalProps {
  perfil: IPerfil;
  onClose: () => void;
  onSaved: (descricao: string) => void;
}

const EditDescricaoModal: React.FC<EditModalProps> = ({ perfil, onClose, onSaved }) => {
  const [descricao, setDescricao] = useState(perfil.descricao);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const res = await api.updatePerfilAcesso(perfil.nome, { descricao });
    setSaving(false);
    if (res.success) {
      onSaved(descricao);
      onClose();
    } else {
      notify.error('Erro ao salvar.');
    }
  };

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Editar Descrição — {perfil.label}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group>
          <Form.Label>Descrição do Perfil</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={descricao}
            onChange={e => setDescricao(e.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? <Spinner as="span" animation="border" size="sm" /> : 'Salvar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ── Modal de edição de permissões (4Comply only) ──────────────────────────────

interface EditPermModalProps {
  perfil: IPerfil;
  permissao: IPermissao;
  onClose: () => void;
  onSaved: (updated: IPermissao) => void;
}

type AcaoKey = 'visualizar' | 'criar' | 'editar' | 'desativar' | 'excluir';
const ACOES: AcaoKey[] = ['visualizar', 'criar', 'editar', 'desativar', 'excluir'];

const EditPermissaoModal: React.FC<EditPermModalProps> = ({ perfil, permissao, onClose, onSaved }) => {
  const [perm, setPerm] = useState<IPermissao>({ ...permissao });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const payload: Record<string, boolean> = {};
    ACOES.forEach(a => { payload[a] = perm[a]; });
    const res = await api.updatePermissao(perfil.nome, permissao.modulo, payload);
    setSaving(false);
    if (res.success) {
      onSaved(perm);
      onClose();
    } else {
      notify.error('Erro ao salvar permissão.');
    }
  };

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Permissões — {perfil.label} / {permissao.modulo}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">Selecione as ações permitidas para este módulo neste perfil.</p>
        {ACOES.map(acao => (
          <Form.Check
            key={acao}
            type="switch"
            id={`perm-${acao}`}
            className="mb-2"
            label={acao.charAt(0).toUpperCase() + acao.slice(1)}
            checked={perm[acao]}
            onChange={e => setPerm(prev => ({ ...prev, [acao]: e.target.checked }))}
          />
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? <Spinner as="span" animation="border" size="sm" /> : 'Salvar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ── Página principal ──────────────────────────────────────────────────────────

const PerfisAcesso: React.FC = () => {
  const { tipoEmpresa } = useInterfaceStore();
  const tipoPessoa: string = (() => {
    try { return JSON.parse(localStorage.getItem('usuarioData') || '{}').tipo_pessoa || ''; }
    catch { return ''; }
  })();

  const is4Comply   = tipoEmpresa === '4Comply';
  const canEditDesc = is4Comply || tipoPessoa === 'Administrador';
  const canEditPerm = is4Comply;

  const [perfis, setPerfis] = useState<IPerfil[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingDescPerfil, setEditingDescPerfil] = useState<IPerfil | null>(null);
  const [editingPerm, setEditingPerm] = useState<{ perfil: IPerfil; permissao: IPermissao } | null>(null);

  useEffect(() => {
    api.getPerfisAcesso().then(res => {
      if (res.success && Array.isArray(res.data)) {
        setPerfis(res.data as IPerfil[]);
      } else {
        setError('Erro ao carregar perfis de acesso.');
      }
      setLoading(false);
    });
  }, []);

  const handleDescSaved = (perfilNome: string, descricao: string) => {
    setPerfis(prev => prev.map(p => p.nome === perfilNome ? { ...p, descricao } : p));
  };

  const handlePermSaved = (perfilNome: string, updated: IPermissao) => {
    setPerfis(prev => prev.map(p =>
      p.nome === perfilNome
        ? { ...p, permissoes: p.permissoes.map(pm => pm.modulo === updated.modulo ? updated : pm) }
        : p
    ));
  };

  if (loading) {
    return (
      <Container fluid className="p-4 text-center">
        <Spinner animation="border" /> <span>Carregando perfis...</span>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="mb-4">
        <h1 className="mb-0">Perfis de Acesso</h1>
        <p className="text-muted">
          Visualize e gerencie os perfis de acesso e suas permissões por módulo.
          {!canEditPerm && (
            <span className="ms-2 text-muted small">
              (Apenas o usuário 4Comply pode alterar permissões)
            </span>
          )}
        </p>
      </div>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      <Row className="g-4">
        {perfis.map(perfil => {
          const badge = nivelBadge(perfil.nivel);
          return (
            <Col key={perfil.nome} md={12}>
              <Card className={!perfil.ativo ? 'opacity-50' : ''}>
                <Card.Header>
                  <Row className="align-items-center">
                    <Col>
                      <h5 className="mb-0 d-flex align-items-center gap-2">
                        {perfil.label}
                        <Badge bg={badge.bg} className="fs-6 fw-normal">{badge.text}</Badge>
                        {!perfil.ativo && <Badge bg="secondary">Inativo</Badge>}
                      </h5>
                    </Col>
                    {canEditDesc && (
                      <Col xs="auto">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setEditingDescPerfil(perfil)}
                        >
                          Editar Descrição
                        </Button>
                      </Col>
                    )}
                  </Row>
                </Card.Header>
                <Card.Body>
                  {perfil.descricao && (
                    <p className="text-muted mb-3">{perfil.descricao}</p>
                  )}

                  <Table size="sm" bordered hover responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Módulo</th>
                        <th className="text-center">Visualizar</th>
                        <th className="text-center">Criar</th>
                        <th className="text-center">Editar</th>
                        <th className="text-center">Desativar</th>
                        <th className="text-center">Excluir</th>
                        {canEditPerm && <th className="text-center" style={{ width: 80 }}>Ações</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {perfil.permissoes
                        .sort((a, b) => a.modulo.localeCompare(b.modulo))
                        .map(pm => (
                          <tr key={pm.modulo}>
                            <td>{pm.modulo}</td>
                            <td className="text-center"><CheckIcon value={pm.visualizar} /></td>
                            <td className="text-center"><CheckIcon value={pm.criar} /></td>
                            <td className="text-center"><CheckIcon value={pm.editar} /></td>
                            <td className="text-center"><CheckIcon value={pm.desativar} /></td>
                            <td className="text-center"><CheckIcon value={pm.excluir} /></td>
                            {canEditPerm && (
                              <td className="text-center">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => setEditingPerm({ perfil, permissao: pm })}
                                >
                                  Editar
                                </Button>
                              </td>
                            )}
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Modal edição de descrição */}
      {editingDescPerfil && (
        <EditDescricaoModal
          perfil={editingDescPerfil}
          onClose={() => setEditingDescPerfil(null)}
          onSaved={desc => handleDescSaved(editingDescPerfil.nome, desc)}
        />
      )}

      {/* Modal edição de permissões */}
      {editingPerm && (
        <EditPermissaoModal
          perfil={editingPerm.perfil}
          permissao={editingPerm.permissao}
          onClose={() => setEditingPerm(null)}
          onSaved={updated => handlePermSaved(editingPerm.perfil.nome, updated)}
        />
      )}
    </Container>
  );
};

export default PerfisAcesso;
